import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export const pavoiCommercialInvoiceFileDataSchema = yup.object().shape({
  exporter: yup.object().shape({
    taxIdNumber: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    telephone: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    countryTerritory: yup.string().nullable().optional(),
    contactName: yup.string().nullable().optional(),
    partiesRelated: yup.string().nullable().optional(),
    partiesNonRelated: yup.string().nullable().optional(),
  }),
  shipmetSummary: yup.object().shape({
    shipDate: yup.string().nullable().optional(),
    trackingNumber: yup.string().nullable().optional(),
    invoiceNumber: yup.string().nullable().optional(),
    purchaseOrderNumber: yup.string().nullable().optional(),
    paymentTerms: yup.string().nullable().optional(),
    billOfLading: yup.string().nullable().optional(),
    purposeOfShipment: yup.string().nullable().optional(),
  }),
  consignee: yup.object().shape({
    taxIdNumber: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    contactName: yup.string().nullable().optional(),
    telephone: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    countryTerritory: yup.string().nullable().optional(),
  }),
  soldTo: yup.object().shape({
    sameAsConsignee: yup.string().nullable().optional(),
    taxIdNumber: yup.string().nullable().optional(),
    companyName: yup.string().nullable().optional(),
    addressLine1: yup.string().nullable().optional(),
    addressLine2: yup.string().nullable().optional(),
    city: yup.string().nullable().optional(),
    state: yup.string().nullable().optional(),
    zipCode: yup.string().nullable().optional(),
    country: yup.string().nullable().optional(),
    contactName: yup.string().nullable().optional(),
    telephone: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
    countryTerritory: yup.string().nullable().optional(),
  }),
  broker: yup.object().shape({
    name: yup.string().nullable().optional(),
    telephone: yup.string().nullable().optional(),
    contactName: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  duties: yup.object().shape({
    exporter: yup.string().nullable().optional(),
    consignee: yup.string().nullable().optional(),
    other: yup.string().nullable().optional(),
    otherNote: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        noOfPackages: yup.string().nullable().optional(),
        noOfUnits: yup.string().nullable().optional(),
        netWeight: yup.string().nullable().optional(),
        unitOfMeasure: yup.string().nullable().optional(),
        skuOrItemNumber: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        harmonized: yup.string().nullable().optional(),
        countryOf: yup.string().nullable().optional(),
        unit: yup.string().nullable().optional(),
        total: yup.string().nullable().optional(),
      }),
    )
    .required(),
  totalPackages: yup.string().nullable().optional(),
  totalUnits: yup.string().nullable().optional(),
  totalNetWeight: yup.string().nullable().optional(),
  totalGrossWeight: yup.string().nullable().optional(),
  termsOfSale: yup.string().nullable().optional(),
  subtotal: yup.string().nullable().optional(),
  insurance: yup.string().nullable().optional(),
  freight: yup.string().nullable().optional(),
  packing: yup.string().nullable().optional(),
  handling: yup.string().nullable().optional(),
  other: yup.string().nullable().optional(),
  total: yup.string().nullable().optional(),
  declarationStatement: yup.string().nullable().optional(),
  specialInstructions: yup.string().nullable().optional(),
  originator: yup.string().nullable().optional(),
  currencyCode: yup.string().nullable().optional(),
})

export type PavoiCommercialInvoiceFileData = yup.InferType<
  typeof pavoiCommercialInvoiceFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<PavoiCommercialInvoiceFileData>

// Updated mapping with unique UUIDs in all fields
export const pavoiCommercialInvoiceFileMappings: RequiredFileData = {
  exporter: {
    taxIdNumber: 'c3fdc3df-9591-4c1b-9eba-4fd0793ae3dc',
    companyName: '0a9b6025-1b07-47c0-9f98-e0f6f2a66c0c',
    addressLine1: '1c235bc3-ef3e-4c7d-bb42-52d622a5e9c4',
    addressLine2: 'c88d95cd-38d8-4fb4-8f48-09e46d2d398a',
    city: '5114a2ef-b8bc-4b76-8d62-5e4eab953c38',
    state: '808b2f09-2644-4722-a4d1-d3540a153d85',
    zipCode: 'b364986e-f6ea-44ad-9c54-19f07ebc0625',
    country: '4c8d81c8-05a3-42b1-9a1c-b5e94f912349',
    telephone: '6d85b7b0-a0f1-4dac-a1f6-9ad58e5923f6',
    emailAddress: 'c825fd48-f0d0-4f10-a1e8-7f7f5eb3ba92',
    countryTerritory: '3fa1fe19-07b6-4094-909f-2b4ed3ed44d4',
    contactName: 'bcf8ff43-4b37-4bc2-b005-dbb438673e21',
    partiesRelated: '5fa35ccb-f931-4c79-9a39-8999a3ddfbaf',
    partiesNonRelated: '3a5d9321-f990-45cd-a088-1a0151c0f9d1',
  },
  shipmetSummary: {
    shipDate: 'c3065c02-181c-4e98-9f36-2aca5efc2f8a',
    trackingNumber: 'fa93e4f7-9e0d-44fd-a258-4d776133995d',
    invoiceNumber: '47cd758c-a248-4b2c-b5b3-d0d91c7af3d0',
    purchaseOrderNumber: '51f8d95d-c1dc-4e63-87b4-d2c875e8df72',
    paymentTerms: '39129c23-3865-48db-8216-d5c370b06abe',
    billOfLading: 'b8e1503f-81bf-4b3a-afd2-7cb2c576d61f',
    purposeOfShipment: 'b91df17c-fd7b-49aa-9f52-8c68413fc060',
  },
  consignee: {
    taxIdNumber: '2d6fa2ac-35e5-4e18-a7a2-953841932c21',
    companyName: 'ee4bf410-1fa3-4a6f-94d9-0df39e1f0b79',
    addressLine1: 'd45eb7cb-b0bb-4744-bf04-89d88dd1fafa',
    addressLine2: 'b0abed86-7061-4cc0-911a-3437d57f9c28',
    city: 'd2f53d9f-2617-40ea-b52a-52e54a52ece1',
    state: '57f3e04c-3a81-4d3d-a810-4fb68220204b',
    zipCode: 'b88832a6-f78d-4e5b-904e-3e423d6aab58',
    country: 'f500d404-c5f5-411f-a63f-5d6bbf6fcd54',
    telephone: '0951f888-d13c-4979-a2a9-4a0873a8ee02',
    contactName: 'f474af93-3d5c-4bdb-9c42-53506017c84d',
    emailAddress: 'd0d6134d-95a1-42a6-a60f-8b02e70dc8db',
    countryTerritory: '5603becd-fd3a-4db2-be00-6ab43ee0c827',
  },
  soldTo: {
    sameAsConsignee: '0d2b37ca-511b-449b-9ab9-35cf8713720a',
    taxIdNumber: 'bc863bbf-8348-42e8-8e89-8dcc8ad81c9c',
    companyName: 'ecdc5a53-13ef-4cfe-868b-cd15c9d63128',
    addressLine1: 'c74e235d-869c-40b9-8da6-f45e8c7d4958',
    addressLine2: 'd1b08978-35fa-44e6-b607-e3675f7c2a63',
    city: 'd788cf5c-1fae-4bea-bc9f-e6ca76418f86',
    state: '5e506f0f-8c05-4359-ab83-bb21b8e4be58',
    zipCode: '8f83488a-4d50-46d7-bf77-47b4cff0c1e7',
    country: '97611c23-ea3a-40b9-b638-514a69ceea11',
    telephone: '4d582a30-cffc-4df7-924c-b9afdc113f87',
    contactName: '34bb6b75-9375-40ed-b3f3-e1d81c90fcf2',
    emailAddress: '96bc629a-faa7-4707-9221-d7073599be1d',
    countryTerritory: '6d2af1e0-662a-44d5-a7db-2d6ca853833b',
  },
  broker: {
    name: '12a31342-a4ad-4b82-8d54-b7246ebf07d4',
    telephone: '25fd37c7-bdf8-40ab-a4e9-80aa87fcb1b7',
    contactName: '4ecb607d-14d2-4f4e-986a-cf3b3d8ea36e',
    emailAddress: 'a4a8fde0-140b-4c9f-97e7-cb1c74b4414d',
  },
  duties: {
    exporter: 'ce887f9c-f654-4882-b0f4-3333bc24c455',
    consignee: '28a297d9-e6f1-41b2-9e22-ccdf5fed8473',
    other: 'e1d24726-a1d7-45fe-b2f8-dbb6db602f59',
    otherNote: '7b605e75-6c29-4dd9-b4f4-f4dd7de23a21',
  },
  items: [
    {
      noOfPackages: '48d3c79f-bdce-4b4f-a36e-e46800fb9c2b',
      noOfUnits: '0e89fb70-21b7-4c78-a3c9-964d86b07ee6',
      netWeight: '4a2b730c-a86a-4c2b-8eb5-648f6bfef8b1',
      unitOfMeasure: '04430fdf-4940-4c88-8a74-0ca90fb2b5d5',
      skuOrItemNumber: 'e7fd0614-15a6-4428-a23f-2e3e8229dfea',
      description: '6adbe6e0-2f5b-48e8-a52d-1694ec2b1e1b',
      harmonized: '2f9ac15e-6c2a-4a44-a034-071e600ecb82',
      countryOf: 'eb3bc69f-ee4b-44fa-a5e1-d961f59bd18a',
      unit: '425d4010-9056-4a71-9ea5-160bea3df91e',
      total: '7884ab93-6b1e-4a6f-a20d-8df65b1dc93d',
    },
  ],
  totalPackages: 'be5103f1-3c94-4929-9d58-c65795f9447d',
  totalUnits: '9ce4ba67-debf-44d2-90c6-343e34e238fa',
  totalNetWeight: '7cb4f861-514c-47f0-a06a-6ecb9ff6c9f0',
  totalGrossWeight: '61d06d8a-e3df-4f7d-9c92-a7dd377a4e0b',
  termsOfSale: '4089c8bf-e3c7-4b92-a9fc-417bfd60bb09',
  subtotal: '51cd6ea0-eed0-4fd3-90e0-9e72ec8c99f0',
  insurance: 'de343c23-7a4d-4acb-917a-c21af0f230e2',
  freight: 'f7f802b6-a680-44e0-9c17-a49be57692a0',
  packing: 'de1b8633-d1c8-4244-bd35-45e763c69b42',
  handling: '0f77559e-3bd3-429b-b0aa-186251c6f907',
  other: 'bcfc659a-9369-4709-a0d0-fec4ef0adc68',
  total: 'ef58c782-b5d0-4db9-a82d-af853b32ed54',
  declarationStatement: '801c99fa-90b3-4bdd-bcb3-b64c93eba4ae',
  specialInstructions: '2dc6a762-3d66-4074-83c2-3190b1847cf3',
  originator: '4d9c9afb-d7f4-4fe4-a9ec-e47ef3c1335f',
  currencyCode: '2c66714a-3741-46ca-95e5-87cd1929fc60',
}
